<script lang="ts" setup>
import { ref, nextTick, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, RouteLocation } from 'vue-router'
import { colors } from '@/modules/tailwind'
import {
  mdiHomeVariantOutline,
  mdiCalendarOutline,
  mdiAccountSettingsOutline
} from '@mdi/js'
import anime from '@/modules/animation'

interface BottomNavItem {
  title: string
  path: string
  icon: {
    name: string
    pathValue: string
  }
}

const { t } = useI18n()

const route = useRoute()
watch(
  route,
  async (newRoute) => {
    await nextTick()
    highlightSelection(newRoute)
  },
  { immediate: true }
)

const navItems: BottomNavItem[] = [
  {
    title: 'Home',
    path: '/home',
    icon: {
      name: 'home',
      pathValue: mdiHomeVariantOutline
    }
  },
  {
    title: 'Events',
    path: '/events',
    icon: {
      name: 'events',
      pathValue: mdiCalendarOutline
    }
  },
  {
    title: t('navElement.profile'),
    path: '/profile',
    icon: {
      name: 'profile',
      pathValue: mdiAccountSettingsOutline
    }
  }
]

let activeElement: Element | null = null

const bottomNav = ref<HTMLElement | null>(null)

const highlightSelection = (route: RouteLocation) => {
  if (!bottomNav.value) return
  const basePath =
    route.fullPath
      .substring(1) // Get rid of root slash
      .split('/')
      .shift() ?? ''

  let resolvedPath: string

  if (/^events/.test(basePath)) {
    resolvedPath = 'events'
  } else if (/^(profile|settings|admin)/.test(basePath)) {
    resolvedPath = 'profile'
  } else if (/^home/.test(basePath)) {
    resolvedPath = 'home'
  } else {
    resolvedPath = basePath
  }

  const matchingNavElement = bottomNav.value.querySelector(
    `[data-path^="/${resolvedPath}"]`
  )

  if (matchingNavElement === activeElement) return

  if (matchingNavElement) {
    anime({
      easing: 'easeInOutCubic',
      duration: 300,
      targets: matchingNavElement.querySelector('circle[data-background]'),
      r: 16
    })
    matchingNavElement
      .querySelector('[data-title]')
      ?.classList.add('text-transparent')
  }

  if (activeElement) {
    anime({
      easing: 'easeInOutCubic',
      duration: 300,
      targets: activeElement.querySelector('circle[data-background]'),
      r: 0
    })
    activeElement
      .querySelector('[data-title]')
      ?.classList.remove('text-transparent')
  }

  activeElement = matchingNavElement
}
</script>

<template>
  <div class="sticky bottom-0 z-50 bg-gray-800 border-t border-gray-700/50">
    <nav ref="bottomNav" class="flex justify-evenly p-2 max-w-3xl mx-auto">
      <RouterLink
        v-for="item in navItems"
        v-slot="{ navigate }"
        :key="item.path"
        :to="item.path"
        replace
        custom
      >
        <div
          class="flex flex-col items-center justify-center cursor-pointer"
          :data-path="item.path"
          :data-testid="'nav-' + item.icon.name"
          @click="navigate"
        >
          <svg role="img" width="24" height="24" viewBox="0 0 24 24">
            <defs>
              <radialGradient :id="`${item.icon.name}-gradient`">
                <stop offset="0%" :stop-color="`${colors.violet['500']}`" />
                <stop offset="70%" :stop-color="`${colors.purple['500']}`" />
              </radialGradient>
            </defs>
            <clipPath :id="`${item.icon.name}-clip-path`">
              <path :d="item.icon.pathValue" />
            </clipPath>
            <circle
              cx="12"
              cy="12"
              r="16"
              fill="currentColor"
              :clip-path="`url(#${item.icon.name}-clip-path)`"
            />
            <circle
              :id="`${item.icon.name}-background`"
              cx="12"
              cy="12"
              r="0"
              data-background
              :fill="`url(#${item.icon.name}-gradient)`"
              :clip-path="`url(#${item.icon.name}-clip-path)`"
            />
          </svg>
          <span
            class="text-xs bg-clip-text bg-gradient-to-b from-violet-500 to-purple-400 transition-colors duration-300 delay-100"
            data-title
          >
            {{ item.title }}
          </span>
        </div>
      </RouterLink>
    </nav>
  </div>
</template>

<i18n>
{
  "en": {
    "navElement": {
      "profile": "Profile"
    }
  },
  "de": {
    "navElement": {
      "profile": "Profil"
    }
  }
}
</i18n>
